import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CreditCard, MonitorSmartphone, Paperclip, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SearchSelect } from '@/components/ui/SearchSelect';
import { toast } from '@/stores/ui';
import { NAV_ITEMS } from '@/components/layout/navigation';
import { supportService, type TicketCategory, type TicketKind, type TicketUrgency } from '@/services/support';
import { vaultService } from '@/services/vault';
import { hasBackend } from '@/lib/supabase';
import { useDialogA11y } from '@/lib/useDialogA11y';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** "Reportar un problema" (VLT-328): dos clics desde cualquier pantalla.
 *  El contexto técnico (ruta, viewport, navegador, idioma) viaja solo —
 *  el usuario cuenta QUÉ pasó, nunca tiene que explicar DÓNDE estaba. */
export function ReportProblemModal({ open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [category, setCategory] = useState<TicketCategory>('app');
  const [subject, setSubject] = useState('');
  const [kind, setKind] = useState<TicketKind>('bug');
  const [urgency, setUrgency] = useState<TicketUrgency>('normal');
  const [area, setArea] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  // VLT-368: adjunto opcional (captura/PDF) — sube al vault en modo real.
  const [file, setFile] = useState<File | null>(null);

  // Módulo preseleccionado según dónde está parado el usuario.
  const currentArea = useMemo(() => {
    const hit = NAV_ITEMS.find((n) => n.to !== '/' && location.pathname.startsWith(n.to));
    return hit?.to ?? location.pathname;
  }, [location.pathname]);

  const close = () => {
    setSubject('');
    setDescription('');
    setFile(null);
    onClose();
  };
  const dialogRef = useDialogA11y(open, close);

  const send = async () => {
    if (subject.trim().length < 3 || !description.trim()) {
      toast(t('support.needFields'), 'info');
      return;
    }
    setSending(true);
    // VLT-368: primero los bytes (si hay adjunto y vault real); si el vault
    // rechaza, se avisa el motivo y el reporte NO se manda a medias.
    let attachment: { key?: string; name: string; sizeLabel: string } | null = null;
    if (file) {
      const sizeLabel = `${Math.max(0.1, file.size / 1024 / 1024).toFixed(1)} MB`;
      if (hasBackend) {
        const up = await vaultService.upload(file);
        if (!up.ok) {
          setSending(false);
          toast(t(up.errorKey), 'info');
          return;
        }
        attachment = { key: up.result.key, name: file.name, sizeLabel };
      } else {
        attachment = { name: file.name, sizeLabel }; // demo: sin bytes, sin key
      }
    }
    const res = await supportService.create({
      attachment,
      category, kind, urgency,
      subject: subject.trim(),
      area: area || currentArea,
      description: description.trim(),
      context: {
        route: location.pathname,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
        language: i18n.language,
      },
    });
    setSending(false);
    if (res.ok) {
      toast(t('support.sent', { no: res.ticketNo }));
      close();
    } else {
      toast(t('errors.generic.what'), 'info');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[85] grid place-items-center px-4" role="dialog" aria-modal="true" aria-label={t('support.reportCta')}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close} className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            className="glass relative max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border/60 p-6"
          >
            <div className="mb-1 flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold">{t('support.modalTitle')}</h2>
              <button type="button" onClick={close} aria-label={t('drawer.close')} className="focus-ring rounded-lg p-1.5 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">{t('support.modalSubtitle')}</p>

            {/* 1 · ¿De qué se trata? */}
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t('support.step1')}</p>
            <div className="mb-5 grid gap-2 sm:grid-cols-2">
              {([
                { key: 'app' as const, icon: MonitorSmartphone, title: t('support.catApp'), hint: t('support.catAppHint') },
                { key: 'billing' as const, icon: CreditCard, title: t('support.catBilling'), hint: t('support.catBillingHint') },
              ]).map((c) => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)} aria-pressed={category === c.key}
                  className={cn(
                    'focus-ring rounded-xl border p-3.5 text-left transition-colors',
                    category === c.key ? 'border-primary/60 bg-primary/10' : 'border-border/60 hover:border-primary/40',
                  )}>
                  <span className="mb-1 flex items-center gap-2">
                    <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-primary/15 text-primary"><c.icon className="h-4 w-4" /></span>
                    <span className="text-sm font-semibold">{c.title}</span>
                  </span>
                  <span className="block text-xs leading-relaxed text-muted-foreground">{c.hint}</span>
                </button>
              ))}
            </div>

            {/* 2 · Cuéntanos */}
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t('support.step2')}</p>
            <label className="mb-3 block">
              <span className="text-xs font-medium text-muted-foreground">{t('support.subject')}</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('support.subjectPlaceholder')}
                className="focus-ring mt-1.5 w-full rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground" />
            </label>
            <div className="mb-3 grid gap-3 sm:grid-cols-3">
              <div>
                <span className="text-xs font-medium text-muted-foreground">{t('support.where')}</span>
                <SearchSelect className="mt-1.5" value={area || currentArea} onChange={setArea} label={t('support.where')}
                  options={NAV_ITEMS.map((n) => ({ value: n.to, label: t(n.labelKey) }))} />
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">{t('support.kind')}</span>
                <SearchSelect className="mt-1.5" value={kind} onChange={(v) => setKind(v as TicketKind)} label={t('support.kind')}
                  options={(['bug', 'question', 'suggestion', 'feature'] as const).map((k) => ({ value: k, label: t(`support.kinds.${k}`) }))} />
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">{t('support.urgency')}</span>
                <SearchSelect className="mt-1.5" value={urgency} onChange={(v) => setUrgency(v as TicketUrgency)} label={t('support.urgency')}
                  options={(['low', 'normal', 'high'] as const).map((u) => ({ value: u, label: t(`support.urgencies.${u}`) }))} />
              </div>
            </div>
            <label className="mb-3 block">
              <span className="text-xs font-medium text-muted-foreground">{t('support.what')}</span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder={t('support.whatPlaceholder')}
                className="focus-ring mt-1.5 w-full resize-none rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground" />
            </label>

            {/* Contexto que viaja solo (transparencia total con el usuario) */}
            <p className="mb-4 rounded-lg bg-muted/30 px-3 py-2 text-[11.5px] text-muted-foreground">
              <span className="font-semibold">{t('support.contextLabel')}</span>{' '}
              <code className="text-[11px]">{location.pathname}</code> · {window.innerWidth}x{window.innerHeight} · {t('support.contextRest')}
            </p>
            {/* VLT-368: adjunto real vía vault (antes: attachmentsSoon) */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <label className="focus-ring inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border/60 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground">
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
                {t('support.attach')}
                <input type="file" accept="image/*,application/pdf,.xlsx,.xls,.csv,.doc,.docx,.txt" className="sr-only"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
              {file && (
                <span className="inline-flex max-w-60 items-center gap-1.5 rounded-full bg-muted/50 px-2.5 py-1 text-xs">
                  <span className="truncate">{file.name}</span>
                  <button type="button" aria-label={t('forms.cancel')} onClick={() => setFile(null)}
                    className="focus-ring rounded-full text-muted-foreground hover:text-danger">×</button>
                </span>
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => { close(); navigate('/support'); }}
                className="focus-ring rounded text-sm font-semibold text-primary underline-offset-2 hover:underline">
                {t('support.myTickets')}
              </button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={close}>{t('forms.cancel')}</Button>
                <Button onClick={() => void send()} loading={sending}>
                  <Send className="h-4 w-4" />{t('support.send')}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
